/**
 * Basket (Strategy Builder) leg identity — brief #1 of the Basket workstream.
 *
 * THE RULE: a leg binds its full contract identity at the moment it is ADDED,
 * and nothing downstream ever re-derives any part of it from current UI state.
 *
 * Before this, a leg was roughly `{ strike, optType, side }` and its
 * tradingsymbol was rebuilt at call time from whichever index/expiry happened
 * to be selected. So adding a leg under NIFTY / 14-Aug and then switching the
 * toolbar to SENSEX silently redefined that row to a different contract — with
 * no visible change — and `/api/margin/basket` priced a basket the user never
 * built. (Hedge netting across different underlyings is meaningless, so the
 * error was not marginal.) Multi-index and calendar structures were also simply
 * unrepresentable, which is most of what a strategy builder is for.
 *
 * Paper-only today, but the LIVE basket inherits this model: a live basket that
 * re-resolved legs against current UI state would place REAL orders in
 * contracts the user did not choose. That is why this is brief #1.
 *
 * `lotSize` is per-leg for the same reason as `symbol`: SENSEX is 20 and NIFTY
 * is 65, so one global lot size across a two-index basket computes the wrong
 * QUANTITY — a real-money quantity error, not a display nit.
 *
 * Pure module: explicit inputs only, no DOM/state/clock. The symbol encoding is
 * delegated to symbols.formatExpiry (the same weekly/monthly encoder the rest of
 * the app and the backend's build_option_symbol() agree on).
 */

import { formatExpiry } from "../symbols";
import { computePnl } from "../paper/positions";

export type OptType = "CE" | "PE";
export type Side = "BUY" | "SELL";

/** Everything about a leg that is fixed the moment it is added. */
export interface BasketLegIdentity {
  /** Fyers base token for the underlying, e.g. "NIFTY" / "SENSEX". */
  underlying: string;
  /** "NSE" / "BSE" — the option's exchange, part of the tradingsymbol. */
  exchange: string;
  /** Raw expiry date, "YYYY-MM-DD". NEVER the current toolbar expiry at read time. */
  expiry: string;
  /** Contract multiplier captured at add (NIFTY 65, SENSEX 20). */
  lotSize: number;
  /** Fyers symbol of the INDEX itself, for the margin call's `underlying` field. */
  underlyingSymbol?: string;
}

export interface BasketLeg extends BasketLegIdentity {
  id: string;
  strike: number;
  optType: OptType;
  side: Side;
  lots: number;
  /** The resolved tradingsymbol — bound, and the ONLY symbol any consumer may use. */
  symbol: string;
  /**
   * Price captured AT ADD, the basis for notional P&L. Never re-read from the
   * live feed afterwards. `null` = no price was available at add (stale feed,
   * unsubscribed contract): such a leg is EXCLUDED from the basket total and
   * flagged, because defaulting to 0 would make the whole basket's P&L wrong in
   * a way nobody would notice.
   */
  entry?: number | null;
  /**
   * Broker tradingsymbol for eventual execution (Flattrade NFO/BFO). Distinct
   * from `symbol`, which is the FYERS symbol used for pricing — the live page
   * needs both namespaces. Resolution is fail-closed and on demand: `undefined`
   * = not yet verified, `null` = verified UNTRADEABLE at the broker. Never
   * substituted with a guess. (This brief places nothing; it exists so a leg can
   * be shown as untradeable before execution lands in brief #5.)
   */
  tsym?: string | null;
  /**
   * The CONTRACT's tick size, captured from the broker alongside `tsym` and
   * `lotSize` at verify time (ORDER-2). Session-only and never persisted — like
   * `tsym`, it is broker truth that must be re-resolved rather than remembered.
   *
   * `undefined` = not yet verified. Pricing falls back to DEFAULT_TICK, which is
   * exactly what the single-order path does, so both paths stay identical.
   */
  tick?: number;
}

/**
 * The tradingsymbol for a leg, resolved WITHIN THE LEG'S OWN identity.
 *
 * Used both at add time and when the user edits a leg's strike/type: an edit
 * must re-resolve inside that leg's own underlying+expiry, never against the
 * toolbar's current selection (which may since have moved to another index).
 */
export function resolveLegSymbol(
  identity: BasketLegIdentity, strike: number | string, optType: OptType,
): string {
  if (!identity || !identity.exchange || !identity.underlying || !identity.expiry) return "";
  const exp = formatExpiry(identity.expiry);
  if (!exp) return "";
  return `${identity.exchange}:${identity.underlying}${exp}${strike}${optType}`;
}

/** Build a fully-identified leg by SNAPSHOTTING the identity handed in. */
export function makeBasketLeg(
  identity: BasketLegIdentity,
  spec: {
    id: string; strike: number | string; optType: OptType; side: Side; lots?: number;
    /** Price at add — the P&L basis. Omit/null when none was available. */
    entry?: number | null;
    /**
     * Explicit pricing symbol. The LIVE page takes this straight off the loaded
     * option chain (ce_symbol/pe_symbol), which is authoritative — better than
     * rebuilding the string. Omit to derive it from the identity (paper's path).
     */
    symbol?: string;
    /** Broker tradingsymbol when already resolved (e.g. added from a resolved card). */
    tsym?: string | null;
  },
): BasketLeg {
  const strike = Number(spec.strike);
  return {
    entry: spec.entry == null || isNaN(Number(spec.entry)) ? null : Number(spec.entry),
    id: spec.id,
    underlying: identity.underlying,
    exchange: identity.exchange,
    expiry: identity.expiry,
    lotSize: Number(identity.lotSize) || 1,
    underlyingSymbol: identity.underlyingSymbol || "",
    strike,
    optType: spec.optType,
    side: spec.side,
    lots: Math.max(1, Number(spec.lots) || 1),
    symbol: spec.symbol || resolveLegSymbol(identity, strike, spec.optType),
    tsym: spec.tsym,
  };
}

/** Quantity from the leg's OWN lot size — never a global/current one. */
export function legQty(leg: BasketLeg): number {
  return (Number(leg.lots) || 0) * (Number(leg.lotSize) || 0);
}

/**
 * Does this leg denote the contract the toolbar is currently showing?
 *
 * Only then may a chain-derived LTP be attributed to it: the option chain on
 * screen belongs to the CURRENT index+expiry, so reading a price out of it for a
 * leg of another underlying would show (and price) a number from the wrong
 * contract — the same class of bug as the symbol itself.
 */
export function legMatchesSelection(
  leg: BasketLeg,
  sel: { underlying?: string; exchange?: string; expiry?: string } | null | undefined,
): boolean {
  if (!leg || !sel) return false;
  return leg.underlying === sel.underlying
    && leg.exchange === sel.exchange
    && leg.expiry === sel.expiry;
}

/**
 * Migration guard. A leg persisted/in flight from before identity binding has no
 * underlying/expiry/symbol/lotSize. It must NOT be "upgraded" by assuming the
 * current selection — that is the original bug wearing a different hat. Callers
 * drop such legs with a visible notice instead.
 */
export function isIdentifiedLeg(leg: unknown): leg is BasketLeg {
  const l = leg as Partial<BasketLeg> | null;
  return !!l && typeof l.underlying === "string" && !!l.underlying
    && typeof l.exchange === "string" && !!l.exchange
    && typeof l.expiry === "string" && !!l.expiry
    && typeof l.symbol === "string" && !!l.symbol
    && typeof l.lotSize === "number" && l.lotSize > 0;
}

/**
 * Re-bind a leg to a DIFFERENT contract (the user edited strike/type).
 *
 * The symbol is re-resolved inside the leg's own underlying+expiry, and the
 * entry price is re-captured from the NEW contract — carrying the old strike's
 * entry across would be silently wrong, since it is the P&L basis for a contract
 * that is no longer the one on the row. `newEntry` of null/undefined leaves the
 * leg with no basis (excluded from the total, flagged) rather than a stale one.
 */
export function rebindLegContract(
  leg: BasketLeg,
  change: { strike?: number | string; optType?: OptType; symbol?: string },
  newEntry?: number | null,
): BasketLeg {
  const strike = change.strike != null ? Number(change.strike) : leg.strike;
  const optType = change.optType || leg.optType;
  return {
    ...leg,
    strike,
    optType,
    symbol: change.symbol || resolveLegSymbol(leg, strike, optType),
    entry: newEntry == null ? null : Number(newEntry),
    tsym: undefined,          // a different contract — any previous verification is void
  };
}

/**
 * Notional P&L for one leg against its captured entry, in the platform's
 * existing sign convention (computePnl: BUY (ltp-entry)*qty, SELL the inverse).
 * Returns null when it cannot be computed — no entry basis, or no live price —
 * so callers can EXCLUDE the leg rather than fold a zero into the total.
 */
export function legNotionalPnl(leg: BasketLeg, ltp: number | null | undefined): number | null {
  if (leg == null || leg.entry == null || ltp == null || isNaN(Number(ltp))) return null;
  return computePnl({ side: leg.side, avg: Number(leg.entry), qty: legQty(leg) }, Number(ltp));
}

export interface BasketPnl {
  /** Sum over legs that HAVE both an entry basis and a live price. */
  total: number;
  /** How many legs contributed. */
  priced: number;
  /** Legs left out (no entry basis, or no live price) — surfaced, never hidden. */
  excluded: BasketLeg[];
}

/**
 * Basket notional P&L. NOTIONAL and GROSS of charges — nothing is executed, and
 * the rest of the platform reports NET (charges.ts / Take Home), so the caller
 * must label this honestly or an unqualified figure reads as comparable when it
 * is not.
 */
export function basketNotionalPnl(
  legs: BasketLeg[], ltpFor: (leg: BasketLeg) => number | null | undefined,
): BasketPnl {
  let total = 0, priced = 0;
  const excluded: BasketLeg[] = [];
  for (const leg of legs || []) {
    const pnl = legNotionalPnl(leg, ltpFor(leg));
    if (pnl == null) { excluded.push(leg); continue; }
    total += pnl;
    priced++;
  }
  return { total, priced, excluded };
}

/** Distinct pricing symbols a basket needs subscribed — the subscribed SET must
 * equal this, not the leg count (two legs on one contract share one stream). */
export function distinctLegSymbols(legs: BasketLeg[]): string[] {
  return [...new Set((legs || []).map(l => l && l.symbol).filter(Boolean) as string[])];
}

/** Split a stored basket into usable legs and the unidentified ones to drop. */
export function partitionIdentifiedLegs(legs: unknown[]): { kept: BasketLeg[]; dropped: unknown[] } {
  const kept: BasketLeg[] = [];
  const dropped: unknown[] = [];
  for (const l of legs || []) (isIdentifiedLeg(l) ? kept : dropped).push(l as BasketLeg);
  return { kept, dropped };
}
